import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";

const PHASES = [
  { key: "inhale1", duration: 2000 },
  { key: "inhale2", duration: 1500 },
  { key: "hold", duration: 1000 },
  { key: "exhale", duration: 6000 },
  { key: "rest", duration: 1500 },
];

const TOTAL_CYCLE = PHASES.reduce((sum, p) => sum + p.duration, 0);
const CYCLES = 3;

const Onboarding = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stage, setStage] = useState("intro"); // intro | breathing | feedback | done
  const [breathPhase, setBreathPhase] = useState("inhale1");
  const [cycleCount, setCycleCount] = useState(0);
  const [scale, setScale] = useState(1);
  const [moodAfter, setMoodAfter] = useState(7);
  const animRef = useRef(null);
  const phaseTimeout = useRef(null);

  const runPhase = useCallback((phaseIndex, cycle) => {
    if (cycle >= CYCLES) {
      setStage("feedback");
      return;
    }
    const phase = PHASES[phaseIndex];
    setBreathPhase(phase.key);
    setCycleCount(cycle + 1);

    // Animate scale based on phase
    if (phase.key === "inhale1") setScale(1.3);
    else if (phase.key === "inhale2") setScale(1.5);
    else if (phase.key === "hold") setScale(1.5);
    else if (phase.key === "exhale") setScale(0.85);
    else if (phase.key === "rest") setScale(1);

    phaseTimeout.current = setTimeout(() => {
      const nextIndex = (phaseIndex + 1) % PHASES.length;
      const nextCycle = nextIndex === 0 ? cycle + 1 : cycle;
      runPhase(nextIndex, nextCycle);
    }, phase.duration);
  }, []);

  const startBreathing = () => {
    setStage("breathing");
    setCycleCount(0);
    runPhase(0, 0);
  };

  useEffect(() => {
    return () => {
      if (phaseTimeout.current) clearTimeout(phaseTimeout.current);
    };
  }, []);

  const handleContinue = () => {
    navigate("/health-assessment");
  };

  const getPhaseLabel = () => {
    switch (breathPhase) {
      case "inhale1": return t("onboarding.breathe.inhaleDeep");
      case "inhale2": return t("onboarding.breathe.inhaleAgain");
      case "hold": return t("onboarding.breathe.hold");
      case "exhale": return t("onboarding.breathe.exhaleSlowly");
      case "rest": return t("onboarding.breathe.rest");
      default: return "";
    }
  };

  if (stage === "intro") {
    return (
      <div className="min-h-screen bg-sky-50 dark:bg-slate-900 flex items-center justify-center px-6" data-testid="onboarding-intro">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-8 rounded-2xl bg-gradient-to-br from-sky-400 to-indigo-400 flex items-center justify-center shadow-lg">
            <Sparkles className="w-10 h-10 text-white" strokeWidth={1.5} />
          </div>
          <h1 className="text-3xl font-light text-sky-900 dark:text-sky-100 mb-4" style={{ fontFamily: "Outfit" }}>
            {t("onboarding.welcome.title")}
          </h1>
          <p className="text-slate-600 dark:text-slate-300 mb-3 text-lg">
            {t("onboarding.welcome.subtitle")}
          </p>
          <p className="text-slate-500 dark:text-slate-400 mb-10 text-sm leading-relaxed">
            {t("onboarding.welcome.description")}
          </p>
          <Button onClick={startBreathing} className="glow-button px-8 py-3 text-base" data-testid="start-breathing-btn">
            {t("onboarding.welcome.startButton")}
          </Button>
          <button onClick={handleContinue} className="block mx-auto mt-4 text-sm text-slate-400 hover:text-sky-500 transition-colors" data-testid="skip-onboarding-btn">
            {t("onboarding.welcome.skip")}
          </button>
        </div>
      </div>
    );
  }

  if (stage === "breathing") {
    return (
      <div className="min-h-screen bg-sky-50 dark:bg-slate-900 flex items-center justify-center px-6" data-testid="onboarding-breathing">
        <div className="max-w-md w-full text-center">
          <p className="text-sm text-sky-500 mb-2 font-medium">
            {t("onboarding.breathe.cycle", { current: cycleCount, total: CYCLES })}
          </p>
          <h2 className="text-2xl font-light text-sky-900 dark:text-sky-100 mb-12" style={{ fontFamily: "Outfit" }}>
            {t("onboarding.breathe.title")}
          </h2>

          {/* Breathing Circle */}
          <div className="relative flex items-center justify-center h-64">
            <div
              className="w-40 h-40 rounded-full bg-gradient-to-br from-sky-300 to-indigo-300 dark:from-sky-600 dark:to-indigo-600 shadow-xl"
              style={{
                transform: `scale(${scale})`,
                transition: breathPhase === "exhale" ? "transform 6s ease-out" : breathPhase === "inhale1" ? "transform 2s ease-in" : breathPhase === "inhale2" ? "transform 1.5s ease-in" : "transform 1s ease",
                opacity: 0.85,
              }}
              data-testid="breathing-circle"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="text-lg font-medium text-sky-800 dark:text-sky-200" style={{ fontFamily: "Outfit" }} data-testid="breath-phase-label">
                {getPhaseLabel()}
              </p>
            </div>
          </div>

          <p className="text-xs text-slate-400 dark:text-slate-500 mt-8">
            {t("onboarding.breathe.instruction")}
          </p>
        </div>
      </div>
    );
  }

  if (stage === "feedback") {
    return (
      <div className="min-h-screen bg-sky-50 dark:bg-slate-900 flex items-center justify-center px-6" data-testid="onboarding-feedback">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          </div>
          <h2 className="text-2xl font-light text-sky-900 dark:text-sky-100 mb-3" style={{ fontFamily: "Outfit" }}>
            {t("onboarding.feedback.title")}
          </h2>
          <p className="text-slate-600 dark:text-slate-300 mb-8">
            {t("onboarding.feedback.question")}
          </p>

          {/* Mood Slider */}
          <div className="mb-8 px-4">
            <input
              type="range"
              min="1"
              max="10"
              value={moodAfter}
              onChange={(e) => setMoodAfter(parseInt(e.target.value))}
              className="w-full h-2 rounded-full appearance-none cursor-pointer bg-sky-100 dark:bg-slate-700 accent-sky-500"
              data-testid="mood-slider"
            />
            <div className="flex justify-between mt-2 text-xs text-slate-400">
              <span>{t("onboarding.feedback.low")}</span>
              <span className="text-2xl font-light text-sky-600 dark:text-sky-400" style={{ fontFamily: "Outfit" }}>{moodAfter}</span>
              <span>{t("onboarding.feedback.high")}</span>
            </div>
          </div>

          <div className="glass-card p-5 mb-8 text-left">
            <p className="text-sm text-sky-800 dark:text-sky-200 font-medium mb-1">
              {t("onboarding.feedback.baseline")}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              {t("onboarding.feedback.explanation")}
            </p>
          </div>

          <Button onClick={handleContinue} className="glow-button px-8 py-3 text-base gap-2" data-testid="continue-btn">
            {t("onboarding.feedback.continue")}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export default Onboarding;
